import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  InputAdornment,
  Paper, 
  Button, 
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getTelegramUser, openLink } from '../services/telegram';
import { getUserByTelegramId } from '../services/api';
import { colors } from '../theme/themeConfig';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import SbpIcon from '../assets/icons/SbpIcon';
import { API_URL } from '../config/env';
import { User } from '../types';
import PageHeader from '../components/layout/PageHeader';

const MIN_PAYMENT = 10;
const PAGE_TITLE = 'Покупка бонусов';
const CARD_COMMISSION_PERCENT = 2.25;
const CARD_PAYMENT_TEXT = `Оплатить картой (+${CARD_COMMISSION_PERCENT}%)`;

const BuyBonusPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [telegramUser, setTelegramUser] = useState<any>(null);
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState<string>("100");
  const [price, setPrice] = useState<number>(10);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const iconSize = isMobile ? 20 : 24;
  const username = telegramUser?.username || user?.telegramUsername || 'username';
  
  // Price per bonus point
  const pricePerPoint = 5;
  
  // Update price when amount changes
  useEffect(() => {
    const amount = inputValue === "" ? 0 : parseInt(inputValue);
    setPrice(amount * pricePerPoint);
  }, [inputValue]);
  
  useEffect(() => {
    const loadUser = async () => {
      try {
        // Get Telegram user
        const tgUser = getTelegramUser();
        // Set telegram user immediately to ensure photoUrl is available
        setTelegramUser(tgUser);
        
        if (tgUser) {
          try {
            // Get user data from API
            const userData = await getUserByTelegramId(tgUser.id);
            setUser(userData);
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };
    
    loadUser();
  }, []);
  
  const handleBack = () => {
    navigate(-1);
  };
  
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Allow empty input or valid numbers
    if (value === "" || /^\d+$/.test(value)) {
      setInputValue(value);
    }
  };
  
  const handlePurchase = async (paymentMethod: 'card' | 'sbp') => {
    const amount = inputValue === "" ? 0 : parseInt(inputValue);
    
    if (amount <= 0) {
      setError('Пожалуйста, укажите количество бонусов');
      return;
    } else if (amount < 2) {
      setError(`Минимальная сумма заказа: ${MIN_PAYMENT} рублей`);
      return;
    }
    
    try {
      setProcessing(true);
      setError(null);
      
      const tgUser = getTelegramUser();
      setTelegramUser(tgUser);
      if (!tgUser) {
        setError('Не удалось получить данные пользователя');
        return;
      }
      
      // Prepare data for API call
      const bonusData = {
        telegramId: tgUser.id,
        amountBonus: amount,
        paymentMethod,
      };
      
      // Make API call to create bonus payment
      const response = await fetch(`${API_URL}/payments/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bonusData),
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.paymentUrl) {
        throw new Error('Ошибка при создании платежа.');
      }
      
      // Open payment URL
      openLink(data.paymentUrl);
      
    } catch (error) {
      console.error('Error creating payment:', error);
      setError('Ошибка при создании платежа');
    } finally {
      setProcessing(false);
    }
  };
  
  if (processing) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }
  
  return (
    <Box
      sx={{
        backgroundColor: colors.background.default,
        minHeight: '100vh',
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PageHeader
        username={username} 
        title={PAGE_TITLE}
        photoUrl={telegramUser?.photoUrl}
      />
      
      <Paper
        sx={{
          backgroundColor: colors.background.paper,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(2),
          width: '100%',
          maxWidth: '500px',
          marginBottom: theme.spacing(2),
        }}
      >
        <Typography
          sx={{
            color: colors.text.primary,
            fontSize: theme.typography.pxToRem(16),
            marginBottom: theme.spacing(1),
          }}
        >
          Количество бонусов
        </Typography>
        <TextField
          fullWidth
          type="text"
          variant="outlined"
          value={inputValue}
          onChange={handleAmountChange}
          placeholder="Введите количество"
          InputProps={{
            endAdornment: <InputAdornment position="end">
              <Typography sx={{ color: colors.text.primary }}>
                бонусов
              </Typography>
            </InputAdornment>,
            sx: {
              backgroundColor: '#2A2A2A',
              borderRadius: theme.shape.borderRadius,
              color: colors.text.primary,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary.main,
                borderWidth: 2,
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none',
              },
            }
          }}
          inputProps={{
            style: { color: colors.text.primary }
          }}
        />
      </Paper>
      
      <Paper
        sx={{
          backgroundColor: colors.background.paper,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(2),
          width: '100%',
          maxWidth: '500px',
          marginBottom: theme.spacing(2),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ color: colors.text.primary }}>
          К оплате
        </Typography>
        <Typography 
          sx={{ 
            color: colors.text.primary,
            fontSize: theme.typography.pxToRem(24),
            fontWeight: 500,
          }}
        >
          {price} ₽
        </Typography>
      </Paper>
      
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          mt: theme.spacing(4),
          gap: theme.spacing(2),
          width: '100%',
        }}
      >
        <Button
          variant="contained"
          fullWidth
          onClick={() => handlePurchase('sbp')}
          disabled={processing || inputValue === "" || parseInt(inputValue) <= 0}
          sx={{
            backgroundColor: colors.primary.main,
            color: '#fff',
            padding: theme.spacing(2),
            borderRadius: theme.shape.borderRadius,
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(142, 36, 170, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
            width: '100%',
            maxWidth: '100%',
          }}
          startIcon={<SbpIcon width={iconSize} height={iconSize} />}
        >
          Оплатить через СБП
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={() => handlePurchase('card')}
          disabled={processing || inputValue === "" || parseInt(inputValue) <= 0}
          sx={{
            backgroundColor: colors.primary.main,
            color: '#fff',
            padding: theme.spacing(2),
            borderRadius: theme.shape.borderRadius,
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(142, 36, 170, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
            width: '100%',
            maxWidth: '100%',
          }}
          startIcon={<CreditCardOutlinedIcon sx={{ fontSize: iconSize }} />}
        >
          {CARD_PAYMENT_TEXT}
        </Button>
      </Box>
      
      {error && (
        <Typography
          sx={{
            color: colors.error.main,
            marginTop: theme.spacing(2),
            textAlign: 'center',
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default BuyBonusPage;
